import React from 'react';

import { captureAndReportError } from '../utils/errorReporting';
import { reloadStaleAssetOnce } from '../utils/staleAssetReload';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    captureAndReportError('Error caught by boundary:', error, errorInfo);
    void reloadStaleAssetOnce(error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-950 p-8">
          <div className="text-red-500 text-xl mb-4">Something went wrong</div>
          <div className="text-neutral-400 text-sm mb-6 max-w-md text-center">
            {this.state.error?.message || 'An unexpected error occurred'}
          </div>
          <details className="text-neutral-500 text-xs max-w-2xl">
            <summary className="cursor-pointer mb-2">Error details</summary>
            <pre className="bg-neutral-900 p-4 rounded overflow-auto">
              {this.state.error?.stack}
            </pre>
          </details>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-6 px-4 py-2 bg-white text-black rounded-lg hover:bg-neutral-200 transition-colors"
          >
            Reload page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
